import classNames from 'classnames';
import React from 'react';

import TickIcon from '@udemy/icons/dist/tick.ud-icon';
import {BlockList, Image} from '@udemy/react-core-components';

import {PersonalPlanLandingPageProps} from '../../personal-plan-landing-page-body.react-component';
import styles from './value-prop.module.less';

export interface ValuePropProps extends PersonalPlanLandingPageProps {
    className?: string;
    textContainerClassName?: string;
    /** Optional intro paragraph. Some rows lead straight from the heading into `listItems`. */
    content?: string;
    heading: string;
    imgSrc: {
        desktopSrc: string;
        desktopSrc2x: string;
        mobileSrc: string;
    };
    imgFit?: 'cover' | 'contain';
    slogan?: string;
    listItems?: string[];
}

export const ValueProp = ({
    imgSrc,
    slogan,
    imgFit = 'cover',
    heading,
    content,
    className,
    textContainerClassName,
    isPPLPRevamp,
    isPPLPCompleteRevamp,
    listItems,
}: ValuePropProps) => {
    const {mobileSrc, desktopSrc, desktopSrc2x} = imgSrc;
    // Complete-revamp assets are full compositions (photo + floating UI baked in).
    // Use contain so those overlays are not cropped by a forced cover box.
    const resolvedImgFit = isPPLPCompleteRevamp ? 'contain' : imgFit;

    return (
        <div
            className={classNames(className, styles['value-prop'], {
                [styles.revamp]: isPPLPRevamp,
                [styles['complete-revamp']]: isPPLPCompleteRevamp,
            })}
        >
            <div className={classNames(styles['column'], styles['image-container'])}>
                <Image
                    src={mobileSrc}
                    width={isPPLPCompleteRevamp ? 372 : 620}
                    height={isPPLPCompleteRevamp ? 295 : 350}
                    alt={''}
                    style={{objectFit: resolvedImgFit}}
                    data-purpose="mobile-image"
                    className="hide-on-desktop"
                />
                <Image
                    src={desktopSrc}
                    srcSet={`${desktopSrc} 1x, ${desktopSrc2x} 2x`}
                    width={isPPLPCompleteRevamp ? 598 : 620}
                    height={isPPLPCompleteRevamp ? 474 : 350}
                    alt={''}
                    style={{objectFit: resolvedImgFit}}
                    data-purpose="desktop-image"
                    className="hide-on-mobile"
                />
            </div>

            <div className={classNames(styles['column'], styles['text'], textContainerClassName)}>
                {slogan && (
                    <h3 className={classNames(!isPPLPRevamp && 'ud-heading-md')}>{slogan}</h3>
                )}
                <h2 className={classNames(!isPPLPRevamp && 'ud-heading-serif-xl', styles['title'])}>
                    {heading}
                </h2>
                {content && <p className="ud-text-md">{content}</p>}
                {listItems && listItems.length > 0 && (
                    <div data-purpose="value-prop-list">
                        <BlockList className={styles['list-items']} padding="tight" size="small">
                            {listItems.map((item) => (
                                <BlockList.Item
                                    key={item}
                                    icon={<TickIcon className={styles.checkmark} label="" />}
                                >
                                    {item}
                                </BlockList.Item>
                            ))}
                        </BlockList>
                    </div>
                )}
            </div>
        </div>
    );
};
